/* eslint-disable jsx-a11y/control-has-associated-label */
import styled from "@emotion/styled/macro";
import { useRef, useEffect, useCallback, useState, useMemo } from "react";
import { useQuill } from "react-quilljs";
import { subscribe, isSupported } from "on-screen-keyboard-detector";
import QuillToolbar from "quill/modules/toolbar";
import { NovelUseCase } from "../../usecases/novelUseCase";
import {
  fromMarkdown,
  restoreFullWidthSpacesInDelta,
} from "../../models/episode";
import { Color, FontSize, Media } from "../styles/enums";
import { HEADER_HEIGHT_SMALL } from "../common/header/header";
import { ActivityModal } from "../common/activityModal";
import { loadWithOrientation, getFileInfo } from "../../utils/imageUtils";
import { useImageUploadWarningModal } from "../../hooks/useImageUploadWarningModal";
import { ImageUploadWarningModal } from "../common/imageUploadWarningModal";
import { RubyInputModal } from "./rubyInputModal";
import "../../assets/editor/editor-teller-theme.css";
import { validateURL } from "../../utils/utils";
import {
  MAX_CHARS_PER_STORY,
  SHOW_REMAINING_CHARS_THRESHOLD,
} from "../../utils/constants";

const EditorWrapper = styled.div`
  height: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const EditorContainer = styled.div`
  font-size: ${FontSize.SIZE_16};
  width: 100%;
  height: 100%;
  overflow-y: hidden;
  flex: 1;
  margin-top: 91px;
  background-color: ${Color.WHITE};

  > div {
    max-height: calc(100vh - ${HEADER_HEIGHT_SMALL} - 35px);
  }

  @media ${Media.SMALL} {
    > div {
      transition: all 200ms ease-out;
    }

    &.ql-editor {
      overflow-y: visible;
    }

    &.ql-container.ql-snow {
      border: 0;
    }
  }
`;

const CharacterCount = styled.div<{ aboveLimit: boolean }>`
  font-size: ${FontSize.SIZE_10};
  color: ${({ aboveLimit }) => (aboveLimit ? Color.RED : Color.ACCENT_500)};
  position: absolute;
  top: 95px;
  right: 12px;
  background-color: ${Color.PRIMARY_TRANSPARENT};
  padding: 3px 6px;
  border-radius: 8px;
`;

const Toolbar = styled.div`
  position: fixed;
  top: 46px;
  background: ${Color.WHITE};
  padding-top: 12px;
  padding-bottom: 12px;
  padding-left: 8px;
  padding-right: 8px;
  width: 100%;
  max-width: 960px;
  text-align: center;
  display: flex;
  justify-content: flex-start;
  border-bottom: 1px solid ${Color.ACCENT_50};
  border-left: 1px solid ${Color.ACCENT_50};
  border-right: 1px solid ${Color.ACCENT_50};
  overflow-x: scroll;
  scrollbar-width: none;

  @media ${Media.SMALLER} {
    justify-content: center;
  }

  button {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 56px;
    padding-left: 16px;
    padding-right: 16px;
    border-right: 1px solid ${Color.SEPARATOR};
    cursor: pointer;

    &:last-of-type {
      border: none;
    }

    &.ql-active {
      svg path {
        fill: #2eaadc;
      }
    }
  }
`;

const VIRTUAL_KEYBOARD_HEIGHT_IOS = "53vh";
const VIRTUAL_KEYBOARD_HEIGHT_ANDROID = "20vh";

const Wrapper = styled.div<{ isIPhone?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100vh;

  /* Virtual keyboard is in place, reserve space */
  &.halfHeight {
    @media ${Media.SMALL} {
      ${EditorContainer} > div {
        ${({ isIPhone }) =>
          `height: calc(100vh - ${HEADER_HEIGHT_SMALL} - ${
            isIPhone
              ? VIRTUAL_KEYBOARD_HEIGHT_IOS
              : VIRTUAL_KEYBOARD_HEIGHT_ANDROID
          });
      max-height: calc(100vh - ${HEADER_HEIGHT_SMALL} - ${
        isIPhone ? VIRTUAL_KEYBOARD_HEIGHT_IOS : VIRTUAL_KEYBOARD_HEIGHT_ANDROID
      });`}
      }
    }
  }
`;

const Debug = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  font-size: 12px;
  font-family: Courier, sans-serif;
  z-index: 9999;
`;

const novelUseCase = new NovelUseCase();

// We will script format in markdown from server, then convert to HTML and paste into the editor
// While editing, content will be in HTML
// When sending back to server, the HTML will be converted to markdown
// Reference: https://github.com/quilljs/quill/issues/2514#:~:text=commented-,on%20Feb%2017%2C%202019,-I%20just%20wanted
interface Props {
  onChange(content: string): void;
  content: string;
  onError(msg: string): void;
  isLoading: boolean;
  isNew: boolean;
  setIsEmptyOrAboveLimit(isNotValid: boolean): void;
}

export const Editor: React.FC<Props> = ({
  onChange,
  content,
  onError,
  isLoading,
  isNew,
  setIsEmptyOrAboveLimit,
}) => {
  const [charCount, setCharCount] = useState<number>(0);
  const [hasFocus, setHasFocus] = useState(false);
  const [contentLoaded, setContentLoaded] = useState(false);
  const [isShowActivity, setShowActivity] = useState<boolean>(false);
  const [hasVirtualKeyboard, setHasVirtualKeyboard] = useState(false);
  const [isVirtualKeyboardVisible, setIsVirtualKeyboardVisible] = useState<
    boolean | null
  >(null);
  const [halfHeight, setHalfHeight] = useState(false);

  const { userAgent } = navigator;
  const isTouchable = "ontouchend" in document;
  const isIPhone =
    /\b(\w*iPhone\w*)\b/.test(userAgent) &&
    /\b(\w*Mobile\w*)\b/.test(userAgent) &&
    isTouchable;

  const modules = useMemo(
    () => ({
      toolbar: {
        container: "#toolbar",
      },
      history: {
        // Enable with custom configurations
        delay: 2500,
        maxStack: 500,
      },
      keyboard: {
        bindings: {
          // タブキーを全角スペース2つに変換（日本語組版に合わせる）
          tab: {
            key: "Tab",
            /* eslint-disable react/no-this-in-sfc, object-shorthand, func-names */
            handler: function (this: { quill: typeof quill }) {
              const range = this.quill?.getSelection(true);
              if (range) {
                // 全角スペース2つ（\u3000\u3000）を挿入
                this.quill?.insertText(range.index, "\u3000\u3000", "user");
                this.quill?.setSelection(range.index + 2, 0);
              }
              return false;
            },
            /* eslint-enable react/no-this-in-sfc, object-shorthand, func-names */
          },
        },
      },
    }),
    [],
  );

  const formats = ["bold", "underline", "italic", "header", "divider", "image"];

  const { Quill, quill, quillRef } = useQuill({ modules, formats });

  // const boldRef = useRef<HTMLButtonElement>(null);
  const undoRef = useRef<HTMLButtonElement>(null);
  const redoRef = useRef<HTMLButtonElement>(null);
  const dividerRef = useRef<HTMLButtonElement>(null);
  const rubyRef = useRef<HTMLButtonElement>(null);

  // Ruby input modal state
  const [isRubyModalOpen, setIsRubyModalOpen] = useState(false);

  const handleUndo = useCallback(() => {
    quill?.history?.undo();
  }, [quill]);

  useEffect(() => {
    let unsubscribe: () => void = () => {
      /* noop */
    };
    if (isSupported()) {
      if (!hasVirtualKeyboard) {
        setHasVirtualKeyboard(true);
      }
      unsubscribe = subscribe((visibility) => {
        setIsVirtualKeyboardVisible(visibility === "visible");
      });
    }
    return unsubscribe;
  }, [hasVirtualKeyboard]);

  useEffect(() => {
    const undoElement = undoRef.current;
    if (undoElement) {
      undoElement.addEventListener("click", handleUndo);
    }
    return () => {
      undoElement?.removeEventListener("click", handleUndo);
    };
  }, [handleUndo, quill]);

  const handleRedo = useCallback(() => {
    quill?.history?.redo();
  }, [quill]);

  useEffect(() => {
    const redoElement = redoRef.current;
    if (redoElement) {
      redoElement.addEventListener("click", handleRedo);
    }
    return () => {
      redoElement?.removeEventListener("click", handleRedo);
    };
  }, [handleRedo, quill]);

  const handleDivider = useCallback(() => {
    if (quill) {
      const range = quill.getSelection(true);
      quill.insertEmbed(range.index, "divider", true, Quill.sources.USER);
      quill.setSelection(range.index + 3, Quill.sources.SILENT);
    }
  }, [Quill, quill]);

  useEffect(() => {
    const dividerElement = dividerRef.current;
    if (dividerElement && quill) {
      const toolbar = quill.getModule("toolbar") as QuillToolbar;
      toolbar.addHandler("divider", handleDivider);
    }
  }, [handleDivider, quill]);

  // Ruby input handlers
  const handleRubyButtonClick = useCallback(() => {
    setIsRubyModalOpen(true);
  }, []);

  const handleRubySubmit = useCallback(
    (text: string, ruby: string) => {
      if (quill && Quill) {
        const formattedRuby = `｜${text}《${ruby}》`;
        const range = quill.getSelection(true);
        quill.insertText(range.index, formattedRuby, Quill.sources.USER);
        quill.setSelection(
          range.index + formattedRuby.length,
          Quill.sources.SILENT,
        );
      }
      setIsRubyModalOpen(false);
    },
    [quill, Quill],
  );

  const handleRubyModalClose = useCallback(() => {
    setIsRubyModalOpen(false);
  }, []);

  useEffect(() => {
    const rubyElement = rubyRef.current;
    if (rubyElement) {
      rubyElement.addEventListener("click", handleRubyButtonClick);
    }
    return () => {
      rubyElement?.removeEventListener("click", handleRubyButtonClick);
    };
  }, [handleRubyButtonClick]);

  // Quill configuration prior to instantiate our current editor
  if (Quill && !quill) {
    // Set toolbar icons
    const icons = Quill.import("ui/icons");
    icons.bold =
      '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M14.9024 11.79C15.8487 11.12 16.5121 10.02 16.5121 9C16.5121 6.74 14.8048 5 12.6097 5H7.9754C7.43882 5 7 5.45 7 6V18C7 18.55 7.43882 19 7.9754 19H13.1268C15.1463 19 16.9902 17.31 16.9999 15.23C17.0097 13.7 16.1707 12.39 14.9024 11.79ZM9.439 7.5H12.3658C13.1756 7.5 13.8292 8.17 13.8292 9C13.8292 9.83 13.1756 10.5 12.3658 10.5H9.439V7.5ZM12.8536 16.5H9.439V13.5H12.8536C13.6634 13.5 14.317 14.17 14.317 15C14.317 15.83 13.6634 16.5 12.8536 16.5Z" fill="#62646E"/></svg>';
    icons.underline =
      '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12.6771 16.4C15.2743 16.0533 17.1429 13.6356 17.1429 10.9244V6.11111C17.1429 5.49778 16.6629 5 16.0714 5C15.48 5 15 5.49778 15 6.11111V11.0222C15 12.5067 14.0314 13.8578 12.6257 14.1511C10.6971 14.5689 9 13.04 9 11.1111V6.11111C9 5.49778 8.52 5 7.92857 5C7.33714 5 6.85714 5.49778 6.85714 6.11111V11.1111C6.85714 14.2844 9.54 16.8178 12.6771 16.4ZM6 19.85C6 20.3389 6.38571 21 6.85714 21H17.1429C17.6143 21 18 20.3389 18 19.85C18 19.3611 17.6143 18.75 17.1429 18.75H6.85714C6.38571 18.75 6 19.3611 6 19.85Z" fill="#62646E"/></svg>';
    icons.italic =
      '<svg width="10" height="14" viewBox="0 0 10 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3.33333 0.5V3H5.175L2.325 11H0V13.5H6.66667V11H4.825L7.675 3H10V0.5H3.33333Z" fill="#62646E"/></svg>';
    icons.header[1] =
      '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M9.36842 6.4C9.36842 7.17467 9.96789 7.8 10.7105 7.8H13.8421V17.6C13.8421 18.3747 14.4416 19 15.1842 19C15.9268 19 16.5263 18.3747 16.5263 17.6V7.8H19.6579C20.4005 7.8 21 7.17467 21 6.4C21 5.62533 20.4005 5 19.6579 5H10.7105C9.96789 5 9.36842 5.62533 9.36842 6.4ZM5.34211 12.4667H6.68421V17.6C6.68421 18.3747 7.28368 19 8.02632 19C8.76895 19 9.36842 18.3747 9.36842 17.6V12.4667H10.7105C11.4532 12.4667 12.0526 11.8413 12.0526 11.0667C12.0526 10.292 11.4532 9.66667 10.7105 9.66667H5.34211C4.59947 9.66667 4 10.292 4 11.0667C4 11.8413 4.59947 12.4667 5.34211 12.4667Z" fill="#62646E"/></svg>';
    icons.divider =
      '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6.75 6C6.3375 6 6 5.55 6 5C6 4.45 6.3375 4 6.75 4H17.25C17.6625 4 18 4.45 18 5C18 5.55 17.6625 6 17.25 6H6.75Z" fill="#62646E"/><path d="M6.75 21C6.3375 21 6 20.55 6 20C6 19.45 6.3375 19 6.75 19H17.25C17.6625 19 18 19.45 18 20C18 20.55 17.6625 21 17.25 21H6.75Z" fill="#62646E"/><circle cx="5.5" cy="12.5" r="1.5" fill="#62646E"/><circle cx="18.5" cy="12.5" r="1.5" fill="#62646E"/><circle cx="12" cy="12.5" r="1.5" fill="#62646E"/></svg>';
    icons.image =
      '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M17.2381 19.0952H3.90476V5.7619H12.4762V3.85714H3.90476C2.85714 3.85714 2 4.71429 2 5.7619V19.0952C2 20.1429 2.85714 21 3.90476 21H17.2381C18.2857 21 19.1429 20.1429 19.1429 19.0952V10.5238H17.2381V19.0952ZM9.81905 16.0762L7.95238 13.8286L5.33333 17.1905H15.8095L12.4381 12.7048L9.81905 16.0762ZM19.1429 3.85714V1H17.2381V3.85714H14.381C14.3905 3.86667 14.381 5.7619 14.381 5.7619H17.2381V8.60952C17.2476 8.61905 19.1429 8.60952 19.1429 8.60952V5.7619H22V3.85714H19.1429Z" fill="#62646E"/></svg>';
    icons.undo =
      '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12.4181 8C10.0412 8 7.88846 8.99 6.22908 10.6L5.53381 8.71C4.96872 8.08 4 8.52 4 9.41V14C4 14.55 4.40363 15 4.89697 15H8.911C9.7093 15 10.1129 13.92 9.54785 13.29L7.83464 12.38C9.08143 11.22 10.6691 10.5 12.4271 10.5C15.2615 10.5 17.7102 12.34 18.8763 15C19.1185 15.56 19.6925 15.84 20.2217 15.64C20.8586 15.41 21.1815 14.6 20.8945 13.92C19.3517 10.42 16.1405 8 12.4181 8Z" fill="#62646E"/></svg>';
    icons.redo =
      '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12.5819 8C14.9588 8 17.1115 8.99 18.7709 10.6L19.4662 8.71C20.0313 8.08 21 8.52 21 9.41V14C21 14.55 20.5964 15 20.103 15H16.089C15.2907 15 14.8871 13.92 15.4522 13.29L17.1654 12.38C15.9186 11.22 14.3309 10.5 12.5729 10.5C9.73848 10.5 7.28976 12.34 6.12371 15C5.88153 15.56 5.30747 15.84 4.77826 15.64C4.14141 15.41 3.8185 14.6 4.10553 13.92C5.64831 10.42 8.85945 8 12.5819 8Z" fill="#62646E"/></svg>';
    icons.ruby =
      '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g clip-path="url(#a)" fill="#62646e"><path d="m6.64102 11.1432c-.52022 0-.74387.3841-.78277.7147-.00486.0827-.00972.6369-.00972 1.5656 0 2.3142-.58342 3.3546-.93348 3.977-.12154.2139-.22364.4084-.22364.6709 0 .5737.42784.9286.82165.9286.35978 0 .55912-.2674.62718-.3549.87514-1.1668 1.28354-3.7388 1.28354-5.1147v-.1118c0-1.4148-.00973-1.5801-.02917-1.6482-.1021-.4035-.36951-.6272-.75359-.6272z"/><path d="m11.323 13.3408c-.3792 0-.6175.2285-.7098.6953l-.0195.0972c-.0972.5688-.1021.5786-.1701.8265-.2723.9821-.71474 1.4634-.99673 1.6822v-4.541c0-.5396 0-1.2738-.80707-1.2738s-.76817.7682-.76817 1.2738v5.3724c0 .491 0 1.4002 1.02099 1.4002.86055 0 1.71138-.8362 2.21698-1.6141.8266-1.2738 1.0113-2.8394 1.0113-3.0727 0-.4084-.2723-.8411-.7828-.8411z"/><path d="m18.2457 16.9288c-.0632 0-.1847.0195-.4181.0632-.0972.0146-.1799.0341-.2237.0389-.5639.0778-1.3856.1313-2.0419.1313-1.0842 0-1.0842-.0778-1.0842-.6904v-1.478c1.1522-.1313 2.2607-.4181 3.2963-.8508.6661-.2723.9772-.4619.9772-1.0453 0-.6515-.3743-.9481-.7195-.9481-.141 0-.248.0486-.4813.1459-.5154.2431-1.444.6758-3.0727.914v-1.2738c0-.5397 0-1.2738-.7974-1.2738-.7973 0-.7682.7682-.7682 1.2738v4.7452c0 2.2608 1.5315 2.2608 2.8831 2.2608.3549 0 2.1538-.0098 2.6789-.2091.3355-.1264.53-.4376.53-.8557 0-.5834-.2869-.9481-.7536-.9481z"/><path d="m8.29403 6.951c1.16463 0 1.46326.78637 1.46326 1.23929 0 .95062-.93569 1.39357-1.88631 1.39357-.18913 0-1.30399 0-1.30399-.82121 0-.39319.32351-.73661.91578-.73661.90085 0 .99541.67191 1.02528.88592.47779-.18913.6072-.45789.6072-.68186 0-.70674-.8262-.70674-.99044-.70674-.83117 0-1.2144.27872-1.53791.50766-.36333.25881-.40314.28369-.5425.28369-.17917 0-.33346-.13935-.33346-.34839 0-.19411.13438-.29862.24885-.37826.89587-.62213 1.04021-.71172 2.33922-1.55782-.62711.08461-1.68225.11945-1.72206.11945-.18913 0-.32849-.13438-.32849-.32848 0-.33347.28867-.33347.433-.33347.07466 0 .41808.00996.48776.00996.38821 0 1.01034-.02986 1.19449-.03982.04977 0 .40314-.01991.41807-.01991.26379 0 .46785.11448.46785.39319 0 .2389-.16922.3683-.31853.4778-.17918.1294-.69182.47282-1.17957.7366.13936-.04977.26876-.09456.5425-.09456zm-.84112 1.61754c-.15927 0-.25383.07466-.25383.15927 0 .13936.22397.27374.50268.27374.04479 0 .07466-.00498.12941-.00996-.00996-.11945-.02987-.42305-.37826-.42305z"/><path d="m16.8197 5.3683c0-.13936.1194-.17419.1842-.17419.2189 0 .3931.47779.3931.59227 0 .16922-.1493.22894-.2637.20903.1094.09457.6221.49771.7216.57734.1593.1294.1593.21899.1593.26876 0 .1742-.1394.35835-.3434.35835-.1344 0-.2041-.07466-.2688-.13936-.0746-.07465-.3932-.433-.4678-.4977.1294.2588.3434.69679.3434 1.30896 0 1.02528-.7267 1.74695-1.7569 1.74695-1.0303 0-1.732-.69679-1.732-1.65238 0-.9556.6818-1.64243.9605-1.84151-.3135.10949-.9058.15926-.9257.15926-.1593 0-.3235-.11447-.3235-.32848 0-.25383.1941-.2837.3583-.30361.4131-.04479.6769-.07465 1.0403-.14931.2538-.05474.2836-.05972.3434-.05972.1294 0 .3932.10452.3932.41309 0 .18913-.1145.28867-.3086.45292-.1444.12442-.4131.40812-.5226.54747-.2787.36831-.3484.76149-.3484 1.02528 0 .55245.4031 1.09495 1.0601 1.09495.4828 0 1.1447-.32351 1.1447-1.22436 0-.16424-.0099-.85605-.4728-1.70215-.1493-.27872-.1642-.30858-.1642-.40812 0-.15429.1493-.30858.3583-.30858.1095 0 .2041.04977.2986.15927.0996.11945.1195.14433.3186.37328-.0846-.14434-.1792-.44296-.1792-.49771zm.7366-.3683c.214 0 .433.50766.433.63706 0 .11945-.0746.21402-.209.21402-.1245 0-.1742-.07964-.2439-.20904 0-.00995-.1742-.34342-.1742-.46287 0-.08461.0498-.14433.1344-.16922.0199 0 .0398-.00995.0597-.00995z"/><path d="m17.058 9.82677-.1215.05736c-.2734.12907-.3905.45537-.2614.72877l.3485.7382c.129.2734.4553.3904.7288.2613l.1215-.0573c.2734-.1291.3904-.4554.2613-.7288l-.3484-.7382c-.1291-.27338-.4554-.3904-.7288-.26133z"/><path d="m18.4389 9.17409-.1215.05736c-.2735.12908-.3905.45537-.2614.7288l.3484.73815c.1291.2734.4554.3904.7288.2614l.1215-.0574c.2735-.1291.3905-.4554.2614-.7288l-.3484-.73813c-.1291-.27343-.4554-.39045-.7288-.26138z"/></g><clipPath id="a"><path d="m0 0h24v24h-24z"/></clipPath></svg>';

    // Add HR extension
    const BlockEmbed = Quill.import("blots/block/embed");
    class DividerBlot extends BlockEmbed {
      static blotName = "divider";

      static tagName = "hr";
    }
    Quill.register(DividerBlot);
  }

  const imageFileRef = useRef<HTMLInputElement | null>(null);

  const contentImageClick = useCallback(() => {
    imageFileRef.current?.click();
  }, []);

  const setEditorFocus = useCallback(() => {
    if (quill) {
      quill.focus();
      setHasFocus(true);
    }
  }, [quill]);

  const contentImageHandler = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      setShowActivity(true);
      if (quill) {
        quill.disable();
        const file = e.target.files?.[0];
        if (file) {
          const imageData = await loadWithOrientation(file);
          const canvas = imageData.image as HTMLCanvasElement;
          const base64 = canvas.toDataURL(file.type);
          const fileInfo = await getFileInfo(base64);
          if (fileInfo.isQR) {
            onError("この画像はアップできません。");
            setShowActivity(false);
            quill.enable();
            return;
          }
          const extension = file.name.split(".").pop() || "jpeg";
          const rpcImage = await novelUseCase.createImageOnServer(
            base64,
            extension,
            fileInfo,
          );
          if (!rpcImage || !validateURL(rpcImage.servingUrl?.value)) {
            onError("画像のアップロードに失敗しました");
          } else {
            const range = quill.getSelection();
            if (range && rpcImage.servingUrl?.value) {
              quill.insertEmbed(range.index, "block", "p", "api");
              quill.insertEmbed(
                range.index + 1,
                "image",
                rpcImage.servingUrl.value,
                "api",
              );
            }
          }
        }
        setShowActivity(false);
        quill.enable();
        setTimeout(() => {
          setHasFocus(false);
        }, 500);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [quill, setHasFocus],
  );

  const scrollToCursor = useCallback(() => {
    if (quillRef.current) {
      const editor = quillRef.current.querySelector("div.ql-editor");
      // Detect current scroll position
      const top = editor.scrollTop;

      // Detect current cursor position
      const selection = window.getSelection();
      const range = selection?.getRangeAt(0);
      const rect = range?.getClientRects()[0];

      // For some weird reason, safari webview scrolls up and the fixed header is lost
      // So we need to scroll to top the first thing on focus
      window.scrollTo(0, 0);
      // If the position is not near the top, scroll there adding a little padding
      if (rect && rect.y > 300) {
        editor.scrollTo({ top: top + 100, left: 0, behavior: "smooth" });
      }
    }
  }, [quillRef]);

  useEffect(() => {
    if (quill) {
      quill.on("text-change", () => {
        const count = quill.getText().length - 1;
        setCharCount(count);
        setIsEmptyOrAboveLimit(count === 0 || count > MAX_CHARS_PER_STORY);
        const html = quillRef.current.firstChild.innerHTML;
        onChange(html);
      });

      quill.on("selection-change", (range, oldRange) => {
        if (range === null && oldRange !== null) {
          setHasFocus(false);
        } else if (range !== null && oldRange === null) {
          setHasFocus(true);
        }
      });

      if (content && content.length > 0) {
        quill.disable();
        const html = fromMarkdown(content);

        // Convert HTML to Delta, then restore PUA placeholders to full-width spaces
        // This is needed because Quill's clipboard processing strips whitespace characters
        // but PUA characters (\uE000) pass through unmodified
        const delta = quill.clipboard.convert({ html });

        // Restore PUA placeholders to full-width spaces in the Delta
        const restoredOps = restoreFullWidthSpacesInDelta(delta.ops);

        // Use setContents instead of dangerouslyPasteHTML to set the restored Delta
        quill.setContents(restoredOps);
        quill.enable();
        setContentLoaded(true);
      }
      const toolbar = quill.getModule("toolbar") as QuillToolbar;
      toolbar.addHandler("image", contentImageClick);

      quill.root.addEventListener("blur", () => {
        setHasFocus(false);
      });

      // ペースト時にタブ文字を全角スペース2つに変換（日本語組版に合わせる）
      // captureフェーズで処理してQuillより先にイベントをキャッチ
      quill.root.addEventListener(
        "paste",
        (e: ClipboardEvent) => {
          const { clipboardData } = e;
          if (!clipboardData) return;

          const text = clipboardData.getData("text/plain");
          // タブ文字が含まれている場合のみ処理
          if (text && text.includes("\t")) {
            e.preventDefault();
            e.stopImmediatePropagation();
            const convertedText = text.replace(/\t/g, "\u3000\u3000");
            const range = quill.getSelection(true);
            if (range) {
              quill.insertText(range.index, convertedText, "user");
              quill.setSelection(range.index + convertedText.length, 0);
            }
          }
        },
        true, // capture phase
      );

      toolbar.container?.addEventListener(
        "mousedown",
        (e: { preventDefault: () => void }) => {
          e.preventDefault();
        },
      );
    }
  }, [
    Quill,
    content,
    contentImageClick,
    onChange,
    quill,
    quillRef,
    scrollToCursor,
    setIsEmptyOrAboveLimit,
  ]);

  useEffect(() => {
    // disable paste html with format after contents has been loaded
    if (quill) {
      if (isNew || (!isLoading && content && contentLoaded)) {
        const Delta = Quill.import("delta");
        // HTML要素のペースト時の処理（画像スキップ）
        quill.clipboard.addMatcher(Node.ELEMENT_NODE, (_, delta) => {
          const newDelta = new Delta();
          delta.ops.forEach((op) => {
            if (
              op.insert &&
              typeof op.insert === "object" &&
              // eslint-disable-next-line no-prototype-builtins
              op.insert.hasOwnProperty("image")
            ) {
              // 画像が貼り付けられた場合はスキップ
              return;
            }
            newDelta.ops.push(op);
          });
          return newDelta;
        });
      }
    }
  }, [Quill, content, contentLoaded, isLoading, isNew, quill]);

  useEffect(() => {
    const toHalf =
      isVirtualKeyboardVisible !== null && isVirtualKeyboardVisible && hasFocus;
    setHalfHeight(toHalf);
    // If the editor contents are long and the user taps below, for some reason
    // the window scrolls down making the header not viaible in iOS
    // This is a very weird effect, because the webview is marked as not scrollable
    // and even disabling scroll in body doesn't have any effect
    // So we force a scroll to top whenever the virtual keyboard is shown

    if (toHalf) {
      // When the editor height is cut in half for making space for the virtual keyboard
      // Try to scroll the editor div to the place where the user tapped
      // So is always visible around the center of the screen at least
      // This has to be executed using timeout (even if is 0). Quill set focus won't work otherwise
      setTimeout(() => {
        setEditorFocus();
        scrollToCursor();
      }, 0);
    }
  }, [
    hasFocus,
    isVirtualKeyboardVisible,
    quillRef,
    scrollToCursor,
    setEditorFocus,
  ]);

  const debug = false;

  const {
    isOpen: isImageWarningModalOpen,
    open: openImageWarningModal,
    close: closeImageWarningModal,
    shouldShowImageWarning,
  } = useImageUploadWarningModal();

  const imageUploadClick = useCallback(
    (e?: React.MouseEvent) => {
      if (shouldShowImageWarning) {
        if (e) {
          e.stopPropagation();
          e.preventDefault();
        }
        setHasFocus(false);
        openImageWarningModal();
      }
    },
    [openImageWarningModal, shouldShowImageWarning],
  );

  const onCloseWarningModal = useCallback(() => {
    closeImageWarningModal();
    setTimeout(() => {
      setHasFocus(true);
      imageFileRef.current?.click();
    }, 1000);
  }, [closeImageWarningModal]);

  const charCountNotice = useMemo(() => {
    if (charCount > SHOW_REMAINING_CHARS_THRESHOLD) {
      return `${charCount.toLocaleString()} / ${MAX_CHARS_PER_STORY.toLocaleString()}文字`;
    }
    return `${charCount.toLocaleString()}文字`;
  }, [charCount]);

  const isAboveTheLimit = useMemo(
    () => charCount > MAX_CHARS_PER_STORY,
    [charCount],
  );

  return (
    <>
      <Wrapper
        className={halfHeight ? "halfHeight" : ""}
        isIPhone={isIPhone}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        {isShowActivity ? <ActivityModal /> : null}
        <Toolbar id="toolbar">
          <button type="button" className="ql-bold" />
          <button type="button" className="ql-underline" />
          <button type="button" className="ql-italic" />
          <button type="button" className="ql-header" value="1" />
          <button type="button" className="ql-divider" ref={dividerRef} />
          <button type="button" className="ql-ruby" ref={rubyRef} />
          <button type="button" className="ql-image" />
          <button type="button" className="ql-undo" ref={undoRef} />
          <button type="button" className="ql-redo" ref={redoRef} />
        </Toolbar>
        <EditorWrapper onClick={setEditorFocus}>
          <input
            ref={imageFileRef}
            type="file"
            accept="image/*"
            onChange={contentImageHandler}
            onClick={imageUploadClick}
            style={{ display: "none" }}
          />

          <EditorContainer ref={quillRef} />
          <CharacterCount aboveLimit={isAboveTheLimit}>
            {charCountNotice}
          </CharacterCount>
        </EditorWrapper>
        {debug ? (
          <Debug>{halfHeight ? "half height" : "full height"}</Debug>
        ) : null}
      </Wrapper>
      {shouldShowImageWarning ? (
        <ImageUploadWarningModal
          open={isImageWarningModalOpen}
          onClose={onCloseWarningModal}
        />
      ) : null}
      <RubyInputModal
        open={isRubyModalOpen}
        onClose={handleRubyModalClose}
        onSubmit={handleRubySubmit}
      />
    </>
  );
};
